import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="w-full min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 text-sm hover:underline">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb />

        <section className="w-full pt-8 pb-14 md:pb-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${category.icon} text-lg`}></i>
                </span>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500 max-w-2xl mt-2">
                {category.description}
              </p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-3">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="flex items-center gap-1 text-primary-600 text-sm font-medium">
                          <span>阅读全文</span>
                          <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}